// ============================================================
// Design Tokens – Single source of truth for the Art-of-Fart UI
// Import these in every component instead of hard-coding values.
// ============================================================

// ---- Fonts ----
export const fonts = {
  body: "'Quicksand', 'Nunito', 'Segoe UI', 'Comic Sans MS', 'Comic Sans', sans-serif",
  heading:
    "'Luckiest Guy', 'Comic Sans MS', 'Comic Sans', 'Quicksand', cursive, sans-serif",
};

// ---- Colors ----
export const colors = {
  // Primary green family (headings, links, success states)
  green: "#188435",        // WCAG AA: 4,6:1 auf Weiß/Creme (vorher #1a8f39 = 4,17:1)
  greenDark: "#0f4c22",
  greenHeading: "#15803d", // WCAG AA auch auf Rosa/Creme-Karten ≥ 4,6:1 (vorher #14a845 = 3,13:1)
  greenText: "#195f15",

  // Gold / amber family (warm accents, gold text, FotD)
  gold: "#956a1c",          // WCAG AA (vorher #b58122 = 3,43:1)
  goldDark: "#8a6714",
  goldLight: "#cd9432",
  goldText: "#7c6b2b",
  goldWarm: "#9b6808", // status / info text (upload, gallery loading) — WCAG AA (vorher #b17709)

  // Cream backgrounds (CSS vars → respond to dark mode)
  cream: "var(--aof-cream)",
  creamLight: "var(--aof-cream-light)",
  creamWarm: "var(--aof-cream-warm)",
  creamOrange: "#fffaf0",
  creamFaq: "#fffbe9", // FAQ / cookie bar bg

  // Gallery green theme
  galleryBg: "#f9ffec",
  galleryCard: "#f7fff4",
  galleryCardAlt: "#ecffe3",
  galleryBorder: "#cbf5ab",
  galleryText: "#2c6b2b",
  galleryHeading: "#195f15",

  // Neutral text (CSS vars → respond to dark mode)
  textDark: "var(--aof-text-dark)",
  textBody: "var(--aof-text-body)",
  textMuted: "var(--aof-text-muted)",

  // Borders
  borderGold: "#ffe28f",
  borderGreen: "#cbf5ab",
  borderLight: "#f1e1b3",

  // Status / feedback
  error: "#c0392b",
  errorBg: "#ffefef",
  successBg: "#eaffea",
  warningBg: "#fff4d6",

  // Vote buttons
  likeBg: "#dcffd3",
  likeBorder: "#8be282",
  likeActiveBg: "#22c55e",
  likeActiveBorder: "#16a34a",
  likeActiveText: "#0a3317", // dunkle Schrift auf Hellgrün: 6,2:1 (weiß war 2,28:1)
  dislikeBg: "#ffe0e0",
  dislikeBorder: "#ffaaaa",

  // Buttons
  btnGreen: "#6ee24e",
  btnGreenHover: "#4ec529",
  ctaGradient:
    "linear-gradient(135deg, #79ff9f 0%, #29d86f 40%, #17c75b 100%)",

  // White overlays (whiteOverlay is a CSS var → responds to dark mode)
  white: "#ffffff",
  whiteOverlay: "var(--aof-content-bg)",

  // Amber / golden text family (footer, golden headings)
  amberDark: "#6d4c00",     // footer brand name, dark labels
  amberText: "#6d5a1a",     // footer link / grid text
  amberHeading: "#70510c",  // footer section headings
  amberMuted: "#84702e",    // muted amber (taglines, smallprint) — WCAG AA
  amberSubtle: "#827040",   // very muted (footer bottom bar) — WCAG AA
  amberHeader: "#8a7a32",   // header subline

  // Additional green shades
  greenLogo: "#199a00",     // header logo (bright)
  greenNav: "#1d840f",      // mobile nav links — WCAG AA
  greenProfile: "#13672a",  // profile / auth link text

  // Moderator badge
  modText: "#7c4a00",
  modBg: "#fff8e1",
  modBorder: "#ffcc66",

  // Cookie consent
  cookieBtnBg: "#ffe4b3",

  // Accent
  orange: "#b45900",        // cookie banner link, misc accents — WCAG AA (vorher #ef7600 = 2,8:1)

  // Neutral grays (for borders, muted UI, dividers)
  gray100: "#f5f5f5",
  gray200: "#e0e0e0",
  gray300: "#ccc",
  gray400: "#aaa",
  gray500: "#888",
  gray600: "#666",
  gray700: "#444",
  gray800: "#333",
  gray900: "#222",

  // Dark surface palette (FartPlayer, FartBomb, dark cards)
  darkBg:      "#0e0d1e",   // deepest background
  darkSurface: "#1a1a2e",   // card / panel background
  darkSurface2:"#12112a",   // input / inner surface
  darkBorder:  "#2d2d4a",   // standard dark border
  darkBorder2: "#3a3a5a",   // slightly lighter border
  darkMuted:   "#5a5a7a",   // muted label text
  darkSubtle:  "#6a6a8a",   // slightly less muted
  darkText:    "#9a9ab8",   // body text on dark
  darkHeading: "#e8e0c8",   // heading text on dark

  // Amber accent (FartPlayer, FartBomb warm tones)
  amber:       "#f6a623",
  amberGlow:   "rgba(246,166,35,0.4)",

  // Adaptive card + input backgrounds (CSS vars → respond to dark mode)
  cardBg:      "var(--aof-card-bg)",
  cardBgGlass: "var(--aof-card-bg-glass)", // frosted-glass card surface (Profile, over illustrated bg)
  cardBorder:  "var(--aof-card-border)",
  inputBg:     "var(--aof-input-bg)",
  inputBorder: "var(--aof-input-border)",

  // Badge-Tier "Platinum" (Profil-Badges Tier 7-8) + adaptive ring track
  // (ersetzt hartcodiertes #ececec im XP-Ring, das im Dark Mode unsichtbar war)
  platinum:  "#6e8fa3",
  ringTrack: "var(--aof-card-border)",

  // Badge-Chip-Familienfarben, die keine Tier-Nummer haben
  // (Custom-Badges = Admin-Freitext, Achievements = fixer Satz von 4)
  customBadgeAccent: "#8b5cf6",
  achievementAccent: "#7c3aed",

  // Table rows (CSS vars → respond to dark mode)
  rowEven:     "var(--aof-row-even)",
  rowOdd:      "var(--aof-row-odd)",
  rowHover:    "var(--aof-row-hover)",
  rowBorder:   "var(--aof-row-border)",

  // Chip/badge semi-transparent overlays (CSS vars → respond to dark mode)
  chipBg:      "var(--aof-chip-bg)",
  chipBorder:  "var(--aof-chip-border)",

  // Section-level backgrounds (CSS vars → respond to dark mode)
  galleryOuterBg:  "var(--aof-gallery-bg)",
  sectionWarmBg:   "var(--aof-section-warm)",
  fotdBg:          "var(--aof-fotd-bg)",

  // AudioPlayer (CSS vars → respond to dark mode)
  audioPlayerBg:     "var(--aof-audioplayer-bg)",
  audioPlayerBorder: "var(--aof-audioplayer-border)",
  audioPlayerTime:   "var(--aof-audioplayer-time)",

  // Hero section (CSS vars → respond to dark mode)
  heroContainer:        "var(--aof-hero-container)",
  heroOverlayMobile:    "var(--aof-hero-overlay-mobile)",
  heroOverlayDesktop:   "var(--aof-hero-overlay-desktop)",
  heroStatsBg:          "var(--aof-hero-stats-bg)",
  heroTrustBg:          "var(--aof-hero-trust-bg)",
};

// ---- Border Radii ----
export const radii = {
  sm: 10,
  md: 16,
  lg: 22,
  xl: 30,
  card: 20,
  pill: 999,
};

// ---- Shadows ----
export const shadows = {
  card: "0 2px 16px rgba(0,0,0,0.07)",
  cardGold: "0 6px 26px #ffe4b366, 0 1px 6px #fffbe7a9",
  cardGreen: "0 6px 26px rgba(166, 212, 153, 0.5)",
  subtle: "0 1px 8px rgba(0,0,0,0.04)",
  glow: "0 8px 50px #bafffc66, 0 1.5px 18px #eaffea3c",
  mainContent: "0 4px 32px rgba(0,0,0,0.08)",
};

// ---- Layout ----
export const layout = {
  maxWidth: 1200,
  contentMaxWidth: 1140,
  breakpoint: 700,
};

// ---- Spacing (4px-Raster) ----
// Immer diese Stufen verwenden statt beliebiger Pixelwerte.
export const space = {
  xxs: 4,
  xs: 8,
  sm: 12,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
};

// ---- Typografie-Skala ----
// Klare Hierarchie: display nur für Seitentitel (Luckiest Guy, ALL-CAPS),
// Überschriften darunter laufen über fonts.body mit Gewicht.
export const type = {
  display: { fontFamily: fonts.heading, fontSize: 32, lineHeight: 1.15, fontWeight: 400, letterSpacing: 0.5 },
  h2:      { fontFamily: fonts.body, fontSize: 20, lineHeight: 1.3, fontWeight: 800 },
  h3:      { fontFamily: fonts.body, fontSize: 16, lineHeight: 1.35, fontWeight: 700 },
  body:    { fontFamily: fonts.body, fontSize: 15, lineHeight: 1.55, fontWeight: 500 },
  caption: { fontFamily: fonts.body, fontSize: 13, lineHeight: 1.45, fontWeight: 600 },
  tiny:    { fontFamily: fonts.body, fontSize: 12, lineHeight: 1.4, fontWeight: 600 },
};

// ---- Fokus-Ring (WCAG: sichtbarer Tastatur-Fokus) ----
// Als Klasse "aof-focusable" über UiGlobalStyles nutzbar; hier der Rohwert
// für Fälle, in denen der Ring inline gebraucht wird.
export const focusRing = {
  outline: "3px solid #f5c800",
  outlineOffset: 2,
};

// ---- Motion ----
export const motion = {
  fast: "140ms ease",
  base: "180ms ease",
  slow: "260ms ease",
};

// ---- Gradients ----
export const gradients = {
  // Global backgrounds (CSS vars → respond to dark mode)
  appBg:  "var(--aof-app-bg)",
  header: "var(--aof-header-bg)",
  footer: "var(--aof-footer-bg)",

  // Section backgrounds (CSS var → responds to dark mode)
  sectionGreen: "var(--aof-section-green)",

  // Section dividers
  dividerGold: "linear-gradient(90deg, transparent, #e8dbb8, transparent)",
  dividerGreen: "linear-gradient(90deg, transparent, #c8e6b0, transparent)",
  dividerBlue: "linear-gradient(90deg, transparent, #c8dfe6, transparent)",

  // Buttons
  loginBtn: "linear-gradient(135deg, #27b300 0%, #35c715 40%, #38d11f 100%)",
  donateBtn: "linear-gradient(135deg, #ffc439 0%, #ffdd63 45%, #ffd173 100%)",
};

// ---- Shared keyframe CSS (inject once via <style> in App.js) ----
export const globalKeyframes = `
  @keyframes fadeIn {
    0% { opacity: 0; transform: translateY(-4px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  @keyframes gentlePulse {
    0%   { transform: scale(1); }
    100% { transform: scale(1.03); }
  }
  @keyframes gentleBounce {
    0%   { transform: scale(1); }
    50%  { transform: scale(1.04) translateY(-3px); }
    100% { transform: scale(1); }
  }
  @keyframes gentleSpin {
    0%   { transform: rotate(-6deg); }
    60%  { transform: rotate(7deg); }
    100% { transform: rotate(0deg); }
  }
  @keyframes float {
    0%   { transform: translateY(0); }
    50%  { transform: translateY(-6px); }
    100% { transform: translateY(0); }
  }
  @keyframes liveBlink {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.3; }
  }
`;
